import Link from '@/lib/siteLink';
import { Calculator, Scale, BookOpen, TrendingUp, CreditCard, Building2, Shield, PiggyBank, ChevronRight, ArrowRight, Percent, IndianRupee, BarChart3, GraduationCap, Briefcase, MessageSquare } from 'lucide-react';
import { FinanceHeader, FinanceFooter } from '@/components/FinanceLayout';
import LatestArticles from '@/components/LatestArticles';
import FundTrackerBand from '@/components/FundTrackerBand';
import { WebSiteSchema } from '@/components/StructuredData';
import SEO from '@/components/SEO';
import AdSlot from '@/components/AdComponents';

const CALCULATORS = [
  { id: 'sip', name: 'SIP Calculator', desc: 'Plan your mutual fund investments', icon: TrendingUp, color: 'emerald' },
  { id: 'emi', name: 'EMI Calculator', desc: 'Calculate loan EMIs instantly', icon: IndianRupee, color: 'blue' },
  { id: 'fd', name: 'FD Calculator', desc: 'Fixed deposit maturity estimator', icon: PiggyBank, color: 'amber' },
  { id: 'tax', name: 'Tax Savings', desc: 'Optimize your tax under 80C/80D', icon: Percent, color: 'violet' },
];

const COMPARE_CATS = [
  { id: 'credit_cards', name: 'Credit Cards', desc: 'Rewards, cashback & travel cards', icon: CreditCard },
  { id: 'demat', name: 'Demat Accounts', desc: 'Brokers for stocks & mutual funds', icon: BarChart3 },
  { id: 'savings', name: 'Bank Accounts', desc: 'Savings & salary accounts compared', icon: Building2 },
  { id: 'insurance', name: 'Insurance', desc: 'Life, health & motor insurance', icon: Shield },
  { id: 'fd_rates', name: 'FD Rates', desc: 'Fixed deposit rates across banks', icon: PiggyBank },
  { id: 'loan_rates', name: 'Loan Rates', desc: 'Home, car & personal loan rates', icon: IndianRupee },
  { id: 'mutual_funds', name: 'Mutual Funds', desc: 'NAV tracker for top schemes', icon: TrendingUp },
];

const LEARN_MODULES = [
  { id: 'investing-101', name: 'Investing 101', lessons: 4, level: 'Beginner' },
  { id: 'mutual-funds', name: 'Mutual Funds Guide', lessons: 4, level: 'Beginner' },
  { id: 'tax-planning', name: 'Tax Planning', lessons: 3, level: 'Intermediate' },
  { id: 'insurance-basics', name: 'Insurance Basics', lessons: 3, level: 'Beginner' },
];

const COLORS = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-600' },
};

export default function FinanceHome() {
  return (
    <>
      <SEO title="YourMoneyWise, Smart Financial Tools, Comparisons & Careers" description="Free SIP, EMI, FD calculators. Compare credit cards, demat accounts, insurance. Finance courses, job board & AI-powered blog. India's trusted financial education platform." site="finance" />
      <WebSiteSchema />
      <FinanceHeader />
      <main className="pt-14">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-gray-950 via-gray-900 to-emerald-950 text-white overflow-hidden" data-testid="finance-hero">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 text-sm text-emerald-300 mb-6">
                <TrendingUp className="w-4 h-4" /> Free Financial Tools
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-manrope font-bold tracking-tight leading-tight">
                Make smarter<br />
                <span className="text-emerald-400">money decisions</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-gray-300 max-w-lg leading-relaxed">
                Calculators, comparisons, courses, jobs, and learning modules to help you save more, earn more, and plan better.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/finance/calculators" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors" data-testid="hero-calculators-btn">
                  <Calculator className="w-4 h-4" /> Explore Calculators
                </Link>
                <Link to="/finance/compare" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors border border-white/10" data-testid="hero-compare-btn">
                  <Scale className="w-4 h-4" /> Compare Products
                </Link>
              </div>
            </div>
          </div>
        </section>

        <FundTrackerBand />

        <LatestArticles textOnly site="finance" title="Latest Finance Articles" basePath="/finance/blog" />

        <AdSlot site="finance" placement="hero_after" maxAds={1} />

        {/* Calculators Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="calculators-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Financial Calculators</h2>
              <p className="text-sm text-gray-500 mt-1">Free tools to plan your finances</p>
            </div>
            <Link to="/finance/calculators" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">View all <ChevronRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {CALCULATORS.map(calc => {
              const c = COLORS[calc.color];
              return (
                <Link key={calc.id} to={`/finance/calculators/${calc.id}`} className="group bg-white border border-gray-200 rounded-xl p-4 sm:p-5 hover:shadow-lg hover:border-gray-300 transition-all" data-testid={`calc-${calc.id}`}>
                  <div className={`w-9 h-9 ${c.bg} ${c.text} rounded-lg flex items-center justify-center mb-3`}>
                    <calc.icon className="w-4 h-4" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">{calc.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 hidden sm:block">{calc.desc}</p>
                  <div className="mt-2 text-xs text-emerald-600 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Calculate <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Compare Products */}
        <section className="bg-gray-50 py-14" data-testid="compare-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Compare Financial Products</h2>
                <p className="text-sm text-gray-500 mt-1">Side-by-side comparison to find the best fit</p>
              </div>
              <Link to="/finance/compare" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">All comparisons <ChevronRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {COMPARE_CATS.map(cat => (
                <Link key={cat.id} to={`/finance/compare?category=${cat.id}`} className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 hover:shadow-md transition-all group" data-testid={`compare-${cat.id}`}>
                  <div className="w-9 h-9 bg-gray-100 text-gray-600 rounded-lg flex items-center justify-center mb-3 group-hover:bg-emerald-50 group-hover:text-emerald-600 transition-colors">
                    <cat.icon className="w-4 h-4" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">{cat.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 hidden sm:block">{cat.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <AdSlot site="finance" placement="between_listings" maxAds={1} />

        {/* Learning Modules */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="learn-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Learning Modules</h2>
              <p className="text-sm text-gray-500 mt-1">Build your financial knowledge step by step</p>
            </div>
            <Link to="/finance/learn" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">All modules <ChevronRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {LEARN_MODULES.map(mod => (
              <Link key={mod.id} to={`/finance/learn/${mod.id}`} className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 hover:shadow-md transition-all" data-testid={`learn-${mod.id}`}>
                <div className="w-9 h-9 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center mb-3">
                  <GraduationCap className="w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm">{mod.name}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full font-medium">{mod.level}</span>
                  <span className="text-[10px] text-gray-400">{mod.lessons} lessons</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Courses & Jobs, Two Column */}
        <section className="bg-gray-50 py-14" data-testid="courses-jobs-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Courses */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-emerald-600" /> Top Courses</h2>
                  <Link to="/finance/courses" className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">View all <ChevronRight className="w-3 h-3" /></Link>
                </div>
                <div className="space-y-2">
                  {[
                    { title: 'Varsity, Stock Market Course', provider: 'Zerodha', free: true, to: '/finance/courses' },
                    { title: 'Financial Markets (Yale)', provider: 'Coursera', free: true, to: '/finance/courses' },
                    { title: 'NCFM Capital Markets', provider: 'NSE Academy', free: false, to: '/finance/courses' },
                    { title: 'CA Foundation', provider: 'ICAI', free: false, to: '/finance/courses' },
                  ].map((c) => (
                    <Link key={c.title} to={c.to} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 hover:shadow-sm transition-all group" data-testid={`home-course-${c.title.slice(0, 10)}`}>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 group-hover:text-emerald-600 transition-colors truncate">{c.title}</h3>
                        <p className="text-[10px] text-gray-400">{c.provider}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${c.free ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-50 text-gray-500'}`}>{c.free ? 'FREE' : 'Paid'}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Jobs */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Briefcase className="w-5 h-5 text-emerald-600" /> Finance Jobs</h2>
                  <Link to="/finance/jobs" className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">View all <ChevronRight className="w-3 h-3" /></Link>
                </div>
                <div className="space-y-2">
                  {[
                    { title: 'Relationship Manager, Wealth', company: 'ICICI Bank', location: 'Mumbai', to: '/finance/jobs' },
                    { title: 'Backend Engineer, Trading', company: 'Zerodha', location: 'Bangalore', to: '/finance/jobs' },
                    { title: 'Product Manager, Mutual Funds', company: 'Groww', location: 'Bangalore', to: '/finance/jobs' },
                    { title: 'Research Analyst, Equity', company: 'HDFC AMC', location: 'Mumbai', to: '/finance/jobs' },
                  ].map((j) => (
                    <Link key={j.title} to={j.to} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 hover:shadow-sm transition-all group" data-testid={`home-job-${j.title.slice(0, 10)}`}>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 group-hover:text-emerald-600 transition-colors truncate">{j.title}</h3>
                        <p className="text-[10px] text-gray-400">{j.company} · {j.location}</p>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-emerald-500 shrink-0 transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lead capture CTA */}
        <section className="bg-emerald-600 py-12" data-testid="finance-cta">
          <div className="max-w-3xl mx-auto px-4 text-center text-white">
            <h2 className="text-2xl font-bold mb-3">Get personalized financial insights</h2>
            <p className="text-emerald-100 text-sm mb-6">Enter your details and we'll help you find the best financial products for your needs.</p>
            <Link to="/finance/calculators" className="inline-flex items-center gap-2 bg-white text-emerald-700 px-6 py-3 rounded-lg font-semibold text-sm hover:bg-emerald-50 transition-colors">
              Start with a Calculator <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      <FinanceFooter />
    </>
  );
}
